import { createContext, useContext, type ReactNode } from 'react';

/**
 * Decouples the schema-form media/entity pickers (§4.7) from the feature modules that
 * implement them. The app shell provides resolvers; widgets call them to get an id back.
 */
export interface PickerApi {
  /** Open the media library picker; resolves to a media id or null if cancelled. */
  /**
   * `trim: 'transparent'` makes an upload made from the opened picker trim the file's
   * transparent margins before it is stored (round 8: partner logos, `x-media-trim`).
   */
  pickMedia: (opts?: { restricted?: boolean; trim?: 'transparent' }) => Promise<string | null>;
  /** Open a content collection picker; resolves to an entity id or null. */
  pickEntity: (collection: string) => Promise<string | null>;
  /** Render a small thumbnail/label for a chosen media id (for the picker field preview). */
  MediaPreview: (props: { id: string | null }) => ReactNode;
  /**
   * The pixel size of a chosen asset, or null while it is unknown.
   *
   * `x-media-min` placements (a teacher's portrait) refuse to publish an image
   * below a threshold, and the form can only say so if it knows how big the
   * picked asset is. It is a resolver on this context rather than a query in the
   * widget for the same reason the preview is: `lib/forms` does not import the
   * media feature. Null covers "not loaded yet" as well as "no such asset", and
   * the caller must treat both as "say nothing" — a spinner rendered as
   * "too small" would be worse than the silence this whole mechanism replaces.
   */
  mediaDimensions: (id: string) => { width: number; height: number } | null;
  /**
   * Why this file would be refused if uploaded (unsupported type, over the
   * deployment's size cap), as a sentence for the editor, or null when it is
   * acceptable. The same pre-flight the media library's dropzone runs.
   */
  checkUpload: (file: File) => string | null;
  /**
   * Upload one image with its English alt text; resolves to the new asset's id.
   * Rejects with an `Error` whose message is already a sentence for the editor
   * (the server's reason for THIS file), so a gallery can show it on the row.
   *
   * It lives on this context, beside `pickMedia`, so a multi-upload never mounts
   * a second media host: the one host stays inside the router (F-28).
   */
  uploadImage: (file: File, alt: string) => Promise<string>;
  /**
   * The "Focal point" control beside a chosen photo (round 8, R8-05), or nothing when
   * the asset is not a public image. Saved on the asset, not on the record.
   */
  FocalControl: (props: { id: string; disabled?: boolean }) => ReactNode;
}

const noop: PickerApi = {
  pickMedia: async () => null,
  pickEntity: async () => null,
  MediaPreview: () => null,
  FocalControl: () => null,
  mediaDimensions: () => null,
  checkUpload: () => null,
  uploadImage: async () => {
    throw new Error('No media host is mounted.');
  },
};

const PickerContext = createContext<PickerApi>(noop);

export function PickerProvider({ value, children }: { value: PickerApi; children: ReactNode }) {
  return <PickerContext.Provider value={value}>{children}</PickerContext.Provider>;
}

export function usePicker(): PickerApi {
  return useContext(PickerContext);
}
